/*
  Reading and writing the days DateField deals in, apart from the component so they can be tested
  and so the component file exports components only.
*/

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** A calendar day, or null when the numbers do not make one (31 June, 29 Feb 2027). */
function makeDay(year: number, month: number, date: number): Date | null {
    const day = new Date(year, month, date);
    return day.getFullYear() === year && day.getMonth() === month && day.getDate() === date
        ? day
        : null;
}

/** "2026-06-16" as a local day; anything after the day (a time) is ignored. */
export function parseIsoDay(value: string | undefined): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
    return match ? makeDay(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
}

/**
 * What a person types: the prototype's "16-Jun-2026" in any case, with spaces or slashes for the
 * dashes and "Sept" for September, or an ISO "2026-06-16". Null for anything else.
 */
export function parseTypedDay(text: string): Date | null {
    const trimmed = text.trim();
    const iso = parseIsoDay(trimmed);
    if (iso && /^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return iso;
    const match = /^(\d{1,2})[\s/-]+([a-z]{3,4})[\s/-]+(\d{4})$/i.exec(trimmed);
    if (!match) return null;
    const month = MONTHS.indexOf(match[2].slice(0, 3).toLowerCase());
    return month === -1 ? null : makeDay(Number(match[3]), month, Number(match[1]));
}

/** "9:05" or "09:05" as "09:05"; null when it is not a time of day. */
export function parseClock(text: string): string | null {
    const match = /^(\d{1,2}):(\d{2})$/.exec(text.trim());
    if (!match || Number(match[1]) > 23 || Number(match[2]) > 59) return null;
    return `${match[1].padStart(2, '0')}:${match[2]}`;
}

/** A local day as "2026-06-16", which is what the forms and the API speak. */
export function isoDay(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
